var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _dec10, _dec11, _dec12, _dec13, _dec14, _class, _class2, _descriptor;

function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }

function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }

function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'proposal-class-properties is enabled and runs after the decorators transform.'); }

/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { Component, html, Prop, Watch } from '@sick-davinci/ce-decorators';
import { AbstractBarChart } from '../AbstractBarChart.js';
import * as QuickChart from '@sick-davinci/quickchart';
import { CssTools } from '../../_utils/CssTools.js';
import { BarChartGroupedLayout } from './BarChartGroupedLayout.js';
import { Thresholds } from '../../_utils/Thresholds.js';
import { ValueAxisBuilder } from '../../_utils/ValueAxisBuilder.js';
import { defaultShorthandLabels } from '../../_utils/NumberFormatter.js';
/**
 * A base class for grouped bar charts
 */

export let AbstractBarChartGrouped = (_dec = Component({
  tag: 'davinci-abstract-bar-chart-grouped',
  abstract: true
}), _dec2 = Reflect.metadata("design:paramtypes", [Object]), _dec3 = Prop(), _dec4 = Reflect.metadata("design:type", Boolean), _dec5 = Watch('shorthandLabels'), _dec6 = Reflect.metadata("design:paramtypes", []), _dec7 = Prop(), _dec8 = Reflect.metadata("design:type", Number), _dec9 = Prop(), _dec10 = Reflect.metadata("design:type", Object), _dec11 = Prop(), _dec12 = Reflect.metadata("design:type", String), _dec13 = Prop(), _dec14 = Reflect.metadata("design:type", Number), _dec(_class = _dec2(_class = (_class2 = class AbstractBarChartGrouped extends AbstractBarChart {
  /**
   * The api reference that is used to delegate the calls
   */
  constructor(seriesColors) {
    super(seriesColors);

    _initializerDefineProperty(this, "shorthandLabels", _descriptor, this);

    this._valueAxisMaxTickCount = 3;
    this._thresholds = new Thresholds();
    this._valueAxisTitle = '';
    this._valueAxisWidth = ValueAxisBuilder.AXIS_DEFAULT_WIDTH;
  }
  /**
   * use abbreviations for big numbers for value-labels (e.g. k=kilo=1000, m=milli=1/1000, etc.)
   *
   * @default defaultShorthandLabels
   */


  shorthandLabelsWatcher() {
    this._updateStyle();
  }

  /**
   * The maximum number of ticks to be used for the value axis.
   *
   * The number of visible ticks depends on
   * - the currently visible value range,
   * - the height of the value axis and
   * - the font used for the tick values (tick values should not overlap).
   *
   * So it might be that there are less ticks visible than the number specified by this property, but there are never more ticks visible.
   *
   * Set this property to a large value (e. g. 100) to ensure that as many ticks as possible are visible.
   *
   * @default 3
   */
  get valueAxisMaxTickCount() {
    return this._valueAxisMaxTickCount;
  }

  set valueAxisMaxTickCount(value) {
    this._valueAxisMaxTickCount = value;

    this._updateStyle();
  }

  /**
   * The thresholds to be shown
   */
  get thresholds() {
    return this._thresholds.thresholds;
  }

  set thresholds(value) {
    this._thresholds.thresholds = value;

    this._updateData();
  }

  /**
   * The title of the value axis.
   *
   * Blank strings will be treated as if no title is set.
   *
   * @default empty string
   */
  get valueAxisTitle() {
    return this._valueAxisTitle;
  }

  set valueAxisTitle(value) {
    this._valueAxisTitle = value;

    this._updateStyle();
  }

  /**
   * The width of the value-axis (in rem)
   *
   * @default ValueAxisBuilder.AXIS_DEFAULT_WIDTH
   */
  get valueAxisWidth() {
    return this._valueAxisWidth;
  }

  set valueAxisWidth(value) {
    this._valueAxisWidth = ValueAxisBuilder.SANITIZE_AXIS_WIDTH(value);

    this._updateStyle();
  }

  _init() {
    var _this$shadowRoot;

    if (!this._api) {
      return;
    }

    const holder = (_this$shadowRoot = this.shadowRoot) === null || _this$shadowRoot === void 0 ? void 0 : _this$shadowRoot.getElementById('holder');

    if (!holder) {
      return;
    }

    holder.appendChild(this._api.holder); // initial update of the data

    this._updateData(); // initial update of the style


    this._updateStyle();
  }
  /**
   * See base class
   */


  componentFirstRender() {
    this._init();
  }

  componentConnected() {
    super.componentConnected();
    this._api = QuickChart.createBarChartGrouped(); // this could be a reattach without a following call for componentFirstRender -> reestablish QuickChart API

    this._init();
  }

  componentDisconnected() {
    super.componentDisconnected(); // We have been detached from the DOM -> clean up

    if (this._api) {
      var _this$shadowRoot2, _this$shadowRoot2$get;

      (_this$shadowRoot2 = this.shadowRoot) === null || _this$shadowRoot2 === void 0 ? void 0 : (_this$shadowRoot2$get = _this$shadowRoot2.getElementById('holder')) === null || _this$shadowRoot2$get === void 0 ? void 0 : _this$shadowRoot2$get.removeChild(this._api.holder);

      this._api.dispose();
    }

    delete this._api;
  }
  /**
   * See base class
   */


  render() {
    var _this$legendLabels;

    // either show chart with surrounding legend or not
    if (((_this$legendLabels = this.legendLabels) === null || _this$legendLabels === void 0 ? void 0 : _this$legendLabels.length) > 0) {
      const colors = Array.from(new Set(this._getSeriesColors()));
      return html`
      <davinci-legend
        placement="N"
        small-font
        compact
        symbols='["box"]'
        symbol-size="${CssTools.REM2PIXEL(.7)}"
        .labels=${this.legendLabels}
        .palette=${this.palette}
        .colors=${colors}>
        ${this._getHolder()}
      </davinci-legend>
      `;
    } else {
      return html`${this._getHolder()}`;
    }
  }
  /**
   * @override
   */


  _updateData() {
    if (this._api === undefined) {
      return;
    }

    const data = this._createData();

    this._api.setData(data);
  }
  /**
   * @override
   */


  _updateStyle() {
    var _this$shadowRoot3;

    if (this._api === undefined) {
      return;
    }

    const host = (_this$shadowRoot3 = this.shadowRoot) === null || _this$shadowRoot3 === void 0 ? void 0 : _this$shadowRoot3.host;

    if (!host) {
      return;
    }

    const hostFontFamily = CssTools.GET_COMPUTED_FONT_FAMILY(host);

    const style = this._createStyle(hostFontFamily);

    this._api.setStyle(style);
  }
  /**
   * @override
   */


  _createData() {
    const data = super._createData();

    data.thresholds = this._thresholds.createFormattedThresholds(this.horizontal, this.shorthandLabels, this.decimalPlaces);
    return data;
  }
  /**
   * @override
   */


  _createStyle(hostFontFamily) {
    const style = super._createStyle(hostFontFamily);

    style.barSize = CssTools.REM2PIXEL(BarChartGroupedLayout.BAR_SIZE);
    style.barGap = CssTools.REM2PIXEL(BarChartGroupedLayout.BAR_GAP);
    style.minGapBetweenGroups = CssTools.REM2PIXEL(BarChartGroupedLayout.MIN_GROUP_DISTANCE);
    style.maxGapBetweenGroups = CssTools.REM2PIXEL(BarChartGroupedLayout.MAX_GROUP_DISTANCE);
    const valueAxisBuilder = new ValueAxisBuilder();
    valueAxisBuilder.fontFamily = hostFontFamily;
    valueAxisBuilder.title = this.valueAxisTitle;
    valueAxisBuilder.horizontal = this.horizontal;
    valueAxisBuilder.maxTickCount = this.valueAxisMaxTickCount;
    valueAxisBuilder.axisWidth = this.valueAxisWidth;
    valueAxisBuilder.shorthandLabels = this.shorthandLabels;
    valueAxisBuilder.decimalPlaces = this.decimalPlaces;
    style.valueAxisStyle = valueAxisBuilder.build();
    style.thresholdStyle = this._thresholds.createThresholdStyle(this.horizontal, hostFontFamily);
    return style;
  }

}, (_descriptor = _applyDecoratedDescriptor(_class2.prototype, "shorthandLabels", [_dec3, _dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return defaultShorthandLabels;
  }
}), _applyDecoratedDescriptor(_class2.prototype, "shorthandLabelsWatcher", [_dec5, _dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "shorthandLabelsWatcher"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "valueAxisMaxTickCount", [_dec7, _dec8], Object.getOwnPropertyDescriptor(_class2.prototype, "valueAxisMaxTickCount"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "thresholds", [_dec9, _dec10], Object.getOwnPropertyDescriptor(_class2.prototype, "thresholds"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "valueAxisTitle", [_dec11, _dec12], Object.getOwnPropertyDescriptor(_class2.prototype, "valueAxisTitle"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "valueAxisWidth", [_dec13, _dec14], Object.getOwnPropertyDescriptor(_class2.prototype, "valueAxisWidth"), _class2.prototype)), _class2)) || _class) || _class);
